import { Component, computed, effect, inject, linkedSignal, OnDestroy, output, signal } from '@angular/core';
import { AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Subject, takeUntil } from 'rxjs';

import { DatePipe } from '@angular/common';
import {
  compareCropFieldByAdvice,
  compareCropFieldByLastSprayDate,
  compareCropFieldByName,
  CropFieldComparator,
  SortMethodType,
} from '../../../../../shared/components/disease-overview-table.component/helpers/sort.helpers';
import {
  AdviceMouseClickEvent,
  CropFieldAdvice,
} from '../../../../../shared/components/disease-overview-table.component/models/models';
import { DiseaseOverviewTableComponent } from '../../../../../shared/components/disease-overview-table.component/table.component';
import { RecalculateButtonComponent } from '../../../../../shared/components/recalculate-button/recalculate-button.component';
import { DownyMildewActionMenuComponent } from '../../../../shared/components/action-menu.component';
import { createDownyMildewTelemetryProperties, DownyMildewAIIdentifiers } from '../../../../shared/constants';
import { DownyMildewSignalStore } from '../../../../signal-store/downy-mildew-signal-store';
import { SprayLegendComponent } from '../../../../../shared/components/spray-legend/spray-legend.component';
import { CropFieldInfo } from '../../../growth-stage-history/growth-stage-history.component';
import { DateService } from '../../../../../shared/services/date.service';
import { CropYearService } from '../../../../../shared/services/crop-year.service';

@Component({
  selector: 'app-downy-mildew-advice-overview',
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.scss',
  imports: [
    RecalculateButtonComponent,
    DownyMildewActionMenuComponent,
    DiseaseOverviewTableComponent,
    DatePipe,
    TranslatePipe,
    SprayLegendComponent,
  ],
})
export class OverviewComponent implements OnDestroy {
  public readonly adviceClick = output<AdviceMouseClickEvent>();
  protected readonly SortMethodType = SortMethodType;
  private readonly cropYearService = inject(CropYearService);
  protected readonly isCropYearReadOnly = this.cropYearService.isReadOnly;
  protected readonly store = inject(DownyMildewSignalStore);
  protected readonly lastCalculatedOn = this.store.downyMildewCalculationDate;
  protected readonly advices = computed((): CropFieldAdvice[] => this.store.downyMildewAdvices());
  private readonly dateService = inject(DateService);
  protected readonly selectedDates = computed(() => {
    const fromDate = this.store.fromDate();
    const toDate = this.store.toDate();
    return fromDate && toDate ? this.dateService.generateDaysBetweenDates(fromDate, toDate) : [];
  });

  private readonly visibilityOverrides = signal<Map<number, boolean>>(new Map());

  protected readonly diseaseCropFields = linkedSignal<
    { id: number; name: string; areaInHectares: number; isVisible: boolean }[] | undefined,
    { id: number; name: string; areaInHectares: number; isVisible: boolean }[]
  >({
    source: () => {
      if (this.store.downyMildewDataIsLoading()) return undefined;
      const overrides = this.visibilityOverrides();
      return this.store.downyMildewAdvices().map((advice) => ({
        id: advice.cropFieldId,
        name: advice.cropFieldName,
        areaInHectares: advice.amountOfHectares,
        isVisible: overrides.has(advice.cropFieldId)
          ? (overrides.get(advice.cropFieldId) ?? advice.isCropFieldVisible)
          : advice.isCropFieldVisible,
      }));
    },
    computation: (source, previous) => source ?? previous?.value ?? [],
  });
  protected readonly isRecalculationDisabled = linkedSignal<boolean | undefined, boolean>({
    source: () => {
      if (this.store.downyMildewDataIsLoading()) return undefined;
      return (
        this.store.isDownyMildewCropFieldsSyncing() ||
        this.advices().length <= 0 ||
        this.isCropYearReadOnly() ||
        !this.store.hasEligibleDownyMildewCropFields()
      );
    },
    computation: (source, previous) => source ?? previous?.value ?? true,
  });

  protected readonly selectedAdvices = signal<CropFieldAdvice[]>([]);
  private readonly localeService = inject(LocaleService);
  protected locale: string = this.localeService.getLocale();
  private readonly ai = inject(AppInsightsServiceInjector);
  private readonly destroy$ = new Subject<void>();
  private previousSelectedDates: Date[] = [];

  protected readonly selectedCropFieldInfos = computed((): CropFieldInfo[] =>
    this.selectedAdvices().map((advice) => ({
      id: advice.cropFieldId,
      name: advice.cropFieldName,
    })),
  );

  constructor() {
    this.localeService.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.locale = this.localeService.getLocale();
    });

    effect(() => {
      const selectedCropFields = this.selectedAdvices();
      if (!selectedCropFields.length) {
        return;
      }

      const cropFieldIds = selectedCropFields.map((cf) => cf.cropFieldId);
      this.store.setGrowthStageSelection(cropFieldIds, selectedCropFields[0].cropYear);
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected onCropFieldClick(event: AdviceMouseClickEvent): void {
    this.ai.trackEvent(
      DownyMildewAIIdentifiers.overviewAdviceCellOpened,
      createDownyMildewTelemetryProperties('overviewAdviceCellOpened', { cropFieldId: event.cropField.cropFieldId }),
    );
    this.adviceClick.emit(event);
    this.store.selectCropField(event.cropField.cropFieldId);
  }

  protected onSelectedDatesChange(dates: Date[]): void {
    this.ai.trackEvent(
      DownyMildewAIIdentifiers.overviewDateNavigationClicked,
      createDownyMildewTelemetryProperties('overviewDateNavigationClicked', {
        direction: this.resolveDateNavigationDirection(dates),
      }),
    );

    this.previousSelectedDates = dates;
    this.store.patchSelectedDates(dates);
  }

  protected onSelectedCropFieldsChange(selectedCropFields: CropFieldAdvice[]): void {
    this.ai.trackEvent(
      DownyMildewAIIdentifiers.overviewCropFieldCheckboxChanged,
      createDownyMildewTelemetryProperties('overviewCropFieldCheckboxChanged', {
        cropFieldCount: selectedCropFields.length,
      }),
    );
    this.selectedAdvices.set(selectedCropFields);
  }

  protected onCropFieldVisibilityChanged(event: { cropFieldId: number; isVisible: boolean }): void {
    this.ai.trackEvent(
      DownyMildewAIIdentifiers.overviewCropFieldFilterChanged,
      createDownyMildewTelemetryProperties('overviewCropFieldFilterChanged', {
        cropFieldId: event.cropFieldId,
        isVisible: event.isVisible,
      }),
    );

    const overrides = new Map(this.visibilityOverrides());
    overrides.set(event.cropFieldId, event.isVisible);
    this.visibilityOverrides.set(overrides);
  }

  protected onRecalculate(): void {
    if (this.isCropYearReadOnly()) {
      return;
    }

    this.ai.trackEvent(
      DownyMildewAIIdentifiers.overviewRecalculateAllClicked,
      createDownyMildewTelemetryProperties('overviewRecalculateAllClicked'),
    );
    const eligibleCropFieldIds = this.store.eligibleDownyMildewCropFieldIds();
    this.store.recalculateDownyMildew(eligibleCropFieldIds);
  }

  protected onSortMethodChanged(sortMethod: CropFieldComparator): void {
    this.ai.trackEvent(
      DownyMildewAIIdentifiers.overviewSortChanged,
      createDownyMildewTelemetryProperties('overviewSortChanged', { sortOption: this.resolveSortOption(sortMethod) }),
    );
  }

  protected onActionMenuOpened(): void {
    this.ai.trackEvent(
      DownyMildewAIIdentifiers.overviewActionMenuOpened,
      createDownyMildewTelemetryProperties('overviewActionMenuOpened'),
    );
  }

  private resolveSortOption(sortMethod: CropFieldComparator): string {
    if (sortMethod === compareCropFieldByAdvice) {
      return SortMethodType.Advice;
    }

    if (sortMethod === compareCropFieldByLastSprayDate) {
      return SortMethodType.Spray;
    }

    if (sortMethod === compareCropFieldByName) {
      return SortMethodType.Name;
    }

    return 'custom';
  }

  private resolveDateNavigationDirection(dates: Date[]): 'next' | 'previous' | 'unknown' {
    if (dates.length < 2 || this.previousSelectedDates.length < 2) {
      return 'unknown';
    }

    const currentStart = dates[0]?.getTime() ?? 0;
    const previousStart = this.previousSelectedDates[0]?.getTime() ?? 0;

    if (currentStart > previousStart) {
      return 'next';
    }

    if (currentStart < previousStart) {
      return 'previous';
    }

    return 'unknown';
  }
}
